import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { FieldDef } from '../../shared/CustomFormV3/types';
import { applyServerErrors } from '../../shared/CustomFormV3/serverErrors';
import {
  ICurrentUser,
  IInvitePreview,
  IRedeemBody,
  IRedeemResult,
  invitesEndpoints,
} from '../../endpoints/invites';

/** Schermata mostrata dalla pagina di invito. */
export type JoinPageState =
  | 'loading'
  /** sessione già attiva: si entra con un clic, senza reinserire nulla */
  | 'signedIn'
  | 'choice'
  | 'form'
  | 'invalid'
  | 'unreachable'
  | 'done';

/** Quale strada ha scelto chi apre il link. */
export type JoinMode = 'login' | 'register';

export interface IJoinFormValues {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  birthdayDate: string;
  gender: string;
}

const EMPTY_VALUES: IJoinFormValues = {
  email: '',
  password: '',
  firstName: '',
  lastName: '',
  birthdayDate: '',
  gender: '',
};

/** Messaggi per i motivi di invalidità che arrivano dal backend. */
const INVALID_MESSAGES: Record<string, string> = {
  not_found: 'Questo link non esiste o non è più attivo.',
  revoked: 'Questo link è stato disattivato da chi te lo ha inviato.',
  expired: 'Questo link è scaduto.',
  full: 'Questo link ha esaurito i posti disponibili.',
};

/** Campi presenti nel form: `applyServerErrors` manda gli altri a toast. */
const KNOWN_FIELDS = Object.keys(EMPTY_VALUES);

/**
 * Logica della pagina di ingresso in un team tramite link.
 *
 * Il percorso è quello classico di questi inviti: prima si sceglie se si ha già
 * un account o se ci si sta registrando, poi si compila solo ciò che quella
 * strada richiede. Nessun rilevamento automatico dell'email — chiedere al
 * server se un indirizzo esiste prima di qualsiasi autenticazione direbbe a
 * chiunque abbia il link quali email sono registrate.
 *
 * Il form è React Hook Form come nel resto del progetto, e gli errori del
 * backend finiscono sotto ai rispettivi campi tramite `applyServerErrors`.
 */
export const useJoinTeam = () => {
  const { code = '' } = useParams<{ code: string }>();

  const [pageState, setPageState] = useState<JoinPageState>('loading');
  const [mode, setMode] = useState<JoinMode>('register');
  const [preview, setPreview] = useState<IInvitePreview | null>(null);
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<IRedeemResult | null>(null);

  /** Utente già connesso in questo browser, se c'è. */
  const [currentUser, setCurrentUser] = useState<ICurrentUser | null>(null);

  const methods = useForm<IJoinFormValues>({
    defaultValues: EMPTY_VALUES,
    mode: 'onChange',
  });
  const { watch, setError, handleSubmit, clearErrors } = methods;

  const values = watch();
  const isRegistering = mode === 'register';

  useEffect(() => {
    let cancelled = false;

    const loadPreview = async () => {
      if (!code) {
        setPageState('invalid');
        return;
      }
      try {
        const { data } = await invitesEndpoints.preview(code);
        if (cancelled) return;
        setPreview(data);

        if (!data.valid) {
          setPageState('invalid');
          return;
        }

        // Se c'è già una sessione, l'identità è provata: non si chiede altro.
        // L'errore qui non è un problema — significa solo "nessuno connesso".
        const me = await invitesEndpoints
          .me()
          .then((r) => r.data)
          .catch(() => null);
        if (cancelled) return;

        if (me?.email) {
          setCurrentUser(me);
          setPageState('signedIn');
        } else {
          setPageState('choice');
        }
      } catch (err) {
        if (cancelled) return;
        // Un server irraggiungibile non è un link inesistente: dirlo all'atleta
        // eviterebbe di mandarlo a cercare un invito nuovo per un guasto nostro.
        const status = (err as { response?: { status?: number } }).response
          ?.status;
        setPageState(status && status < 500 ? 'invalid' : 'unreachable');
      }
    };

    loadPreview();
    return () => {
      cancelled = true;
    };
  }, [code]);

  const chooseMode = useCallback(
    (chosen: JoinMode) => {
      setMode(chosen);
      setGeneralError(null);
      clearErrors();
      setPageState('form');
    },
    [clearErrors],
  );

  /** Entra usando la sessione già attiva: nessuna password da reinserire. */
  const joinAsCurrentUser = useCallback(async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setGeneralError(null);
    try {
      const { data } = await invitesEndpoints.redeemAsMe(code);
      setResult(data);
      setPageState('done');
    } catch (err) {
      const response = (
        err as { response?: { status?: number; data?: { message?: string } } }
      ).response;
      if (response?.status === 401) {
        // La sessione è scaduta mentre era sulla pagina: si torna alla scelta
        setCurrentUser(null);
        setPageState('choice');
        setGeneralError(
          'La tua sessione è scaduta: accedi di nuovo per entrare nel team.',
        );
        return;
      }
      setGeneralError(
        response?.data?.message ||
          'Non è stato possibile completare la richiesta. Riprova.',
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [code, isSubmitting]);

  /** Entra con un altro account: si abbandona la sessione corrente. */
  const useAnotherAccount = useCallback(() => {
    setCurrentUser(null);
    setGeneralError(null);
    setPageState('choice');
  }, []);

  const backToChoice = useCallback(() => {
    setGeneralError(null);
    clearErrors();
    setPageState('choice');
  }, [clearErrors]);

  /**
   * Campi del form nel formato di `CustomFormV3`. Chi accede vede solo email e
   * password; chi si registra anche l'anagrafica che lo schema `User` richiede.
   */
  const layout = useMemo<FieldDef[]>(() => {
    const emailField: FieldDef = {
      key: 'email',
      type: 'text-input',
      inputType: 'email',
      label: 'Email',
      placeholder: 'La tua email',
      size: 12,
    };

    const passwordField: FieldDef = {
      key: 'password',
      type: 'text-input',
      inputType: 'password',
      label: isRegistering ? 'Scegli una password' : 'Password del tuo account',
      placeholder: 'Password',
      size: 12,
    };

    if (!isRegistering) return [emailField, passwordField];

    return [
      emailField,
      { key: 'firstName', type: 'text-input', label: 'Nome', size: 6 },
      { key: 'lastName', type: 'text-input', label: 'Cognome', size: 6 },
      { key: 'birthdayDate', type: 'date', label: 'Data di nascita', size: 6 },
      {
        key: 'gender',
        type: 'single-select',
        label: 'Sesso',
        // il placeholder di default ("Inserisci per cercare…") va a capo in
        // mezza colonna e si sovrappone al controllo
        placeholder: 'Seleziona',
        size: 6,
        options: [
          { label: 'Maschio', value: 'male' },
          { label: 'Femmina', value: 'female' },
        ],
      },
      passwordField,
    ];
  }, [isRegistering]);

  /** Chi accede dà email e password; chi si registra anche l'anagrafica. */
  const isFormComplete = useMemo(() => {
    if (!values.email?.trim() || !values.password) return false;
    if (!isRegistering) return true;
    return Boolean(
      values.firstName?.trim() &&
        values.lastName?.trim() &&
        values.birthdayDate &&
        values.gender,
    );
  }, [values, isRegistering]);

  const submit = handleSubmit(async (formValues) => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    setGeneralError(null);
    try {
      const payload: IRedeemBody = {
        email: formValues.email.trim(),
        password: formValues.password,
        // In accesso l'anagrafica non serve: l'account esiste già
        ...(isRegistering && {
          firstName: formValues.firstName.trim(),
          lastName: formValues.lastName.trim(),
          birthdayDate: formValues.birthdayDate,
          gender: formValues.gender,
        }),
      };

      const { data } = await invitesEndpoints.redeem(code, payload);
      setResult(data);
      setPageState('done');
    } catch (err) {
      const response = (
        err as { response?: { status?: number; data?: { message?: string } } }
      ).response;

      if (response?.status === 401) {
        // Password errata su un account esistente. Se stava provando a
        // registrarsi, l'email era già presa: lo si porta sull'accesso.
        if (isRegistering) {
          setMode('login');
          setGeneralError(
            'Questa email ha già un account LeoPart. Inserisci la sua password per entrare nel team.',
          );
        } else {
          // L'errore vive sotto al campo che lo riguarda: ripeterlo anche nel
          // riquadro generale lo mostrerebbe due volte.
          setError('password', {
            type: 'server',
            message: 'Password errata. Riprova.',
          });
        }
        return;
      }

      // 400 di validazione: gli errori finiscono sotto ai rispettivi campi
      const handled = applyServerErrors(err, setError, {
        knownFields: KNOWN_FIELDS,
        onUnknown: () =>
          setGeneralError(
            'Non è stato possibile completare la richiesta. Riprova.',
          ),
      });

      if (handled) {
        if (!isRegistering) {
          // Stava accedendo ma l'account non esiste: serve la registrazione
          setMode('register');
          setGeneralError(
            'Non risulta un account con questa email: completa i dati qui sotto per registrarti.',
          );
        } else {
          setGeneralError('Completa tutti i campi per registrarti.');
        }
        return;
      }

      setGeneralError(
        response?.data?.message ||
          'Non è stato possibile completare la richiesta. Riprova.',
      );
    } finally {
      setIsSubmitting(false);
    }
  });

  const invalidMessage = useMemo(
    () =>
      INVALID_MESSAGES[preview?.reason ?? 'not_found'] ??
      INVALID_MESSAGES.not_found,
    [preview],
  );

  return {
    methods,
    layout,
    pageState,
    mode,
    isRegistering,
    preview,
    generalError,
    isSubmitting,
    result,
    isFormComplete,
    invalidMessage,
    chooseMode,
    backToChoice,
    submit,
    currentUser,
    joinAsCurrentUser,
    useAnotherAccount,
  };
};
